package org.moxultimate.topwithmox.components.chip

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Filter
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.css.functions.invert
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.utils.trackFilterChipClick
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.theme.MaxChipFontSize
import org.moxultimate.topwithmox.theme.MinChipFontSize

/**
 * Chip
 * Rounded pill used for filters and badges.
 *
 * @param description Label text; also selects the icon for type chips.
 * @param typeChip When true, shows the icon matching [description].
 * @param isSelected Shows the selected gradient background with white text.
 * @param isDark Black background instead of white.
 * @param gradientText Mox gradient text instead of the dark text color.
 * @param compact Smaller padding and font for badges over small cards.
 * @param onChipClick Click handler; null makes the chip non-interactive.
 */
@Composable
fun Chip(
    description: String,
    typeChip: Boolean = false,
    isSelected: Boolean = false,
    isDark: Boolean = false,
    gradientText: Boolean = false,
    compact: Boolean = false,
    onChipClick: (() -> Unit)? = null,
) {
    val iconConfig = remember(description) {
        when (description.lowercase()) {
            "phone"      -> IconConfig("/ic_mobile.png",     16, 18)
            "desktop"    -> IconConfig("/ic_monitor.png",    20, 16)
            "widescreen" -> IconConfig("/ic_widescreen.png", 23, 14)
            "vertical"   -> IconConfig("/ic_vertical.png",  16, 18)
            else         -> null
        }
    }

    Row(
        modifier = PressableButtonStyle.toModifier()
            .then(
                if (compact) Modifier.padding(left = 9.px, top = 5.px, bottom = 4.px, right = 8.px)
                else Modifier.padding(left = 14.px, top = 8.px, bottom = 6.px, right = 12.px)
            )
            .borderRadius(16.px)
            .boxShadow(blurRadius = 2.px, color = Colors.LightGray)
            .then(
                if (onChipClick != null) {
                    Modifier.onClick {
                        trackFilterChipClick(description, isSelected)
                        onChipClick()
                    }
                } else Modifier
            )
            .styleModifier {
                if (isDark) {
                    property("background", "#000000")
                } else if (isSelected) {
                    property("background", "linear-gradient(to right, #FF7034, #F03A85, #8B5CF6)")

                    // This is the old linear gradient for chips
                    //property("background", "linear-gradient(to right, #f47b20 0%, #e61a26 30%, #8b3a8a 55%, #1d71b8 80%, #46b7e6 100%)")
                } else {
                    property("background", "#FFFFFF")
                }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (typeChip) {
            iconConfig?.let { icon ->
                Image(
                    src = icon.src,
                    modifier = Modifier
                        .width(icon.width.px)
                        .height(icon.height.px)
                        .padding(right = 6.px)
                        .filter(if (isSelected) Filter.of(invert()) else Filter.None)
                )
            }
        }

        SpanText(
            text = description,
            modifier = Modifier
                .color(if (isSelected) Colors.White else DarkTextColor)
                .then(if (gradientText) Modifier.moxTextGradient() else Modifier)
                .fontFamily(FontFamily)
                .fontSize(
                    if (compact) 11.px
                    else clamp(min = MinChipFontSize, value = 0.8.vw, max = MaxChipFontSize)
                )
                .fontWeight(FontWeight.Normal)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

private data class IconConfig(
    val src: String,
    val width: Int,
    val height: Int,
)

/**
 * ChipRowScrollStyle
 * Horizontal-scroll style for a chip row. Chips stay on one line — no wrapping.
 * Scrollbar is hidden across all browsers: Firefox (scrollbar-width), Edge (-ms-overflow-style),
 * and Chrome/Safari (::-webkit-scrollbar).
 */
val ChipRowScrollStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("overflow-x", "auto")
            property("overflow-y", "hidden")
            property("flex-wrap", "nowrap")
            property("-webkit-overflow-scrolling", "touch")
            property("scrollbar-width", "none")
            property("-ms-overflow-style", "none")
        }
    }
    cssRule("::-webkit-scrollbar") {
        Modifier.styleModifier {
            property("display", "none")
        }
    }
}
